import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatMs, formatPct, formatUsd } from "../lib/api";
import {
  Bars,
  ErrorState,
  Metric,
  PageHeader,
  SectionHeader,
  SkeletonRows,
  StatusBadge,
  toneForStatus,
} from "../components/ui";

interface AnalyticsResponse {
  windowHours: number;
  summary: {
    total: number;
    success: number;
    successRate: number;
    avgLatencyMs: number;
    avgTtftMs: number;
    freeCount: number;
    totalCostUsd: number;
    fallbacks: number;
    cacheHits: number;
  };
  modelUsage: Array<{
    modelId: string;
    requestedModel: string;
    requests: number;
    success: number;
    avgLatencyMs: number;
    costUsd: number;
    freeCount: number;
  }>;
  providerUsage?: Array<{
    providerId: string;
    providerDisplayName: string;
    providerTypeId: string | null;
    requests: number;
    success: number;
    avgLatencyMs: number | null;
    costUsd: number;
    freeCount: number;
    fallbacks: number;
  }>;
  timeseries: Array<{
    hour: string;
    total: number;
    success: number;
    successRate: number;
    avgLatencyMs: number;
  }>;
}

const WINDOWS = [1, 6, 24, 72];

export default function AnalyticsPage() {
  const [data, setData] = useState<AnalyticsResponse | null>(null);
  const [hours, setHours] = useState(24);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    void api<AnalyticsResponse>(`/api/analytics?hours=${hours}`)
      .then((d) => {
        setData(d);
        setError(null);
      })
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load"))
      .finally(() => setLoading(false));
  }, [hours]);

  return (
    <div className="mx-auto max-w-6xl pb-16">
      <PageHeader
        title="Analytics"
        subtitle="Volume, success, latency, free vs paid, and model mix."
        right={
          <div className="flex rounded-lg border border-canvas-border bg-canvas-raised p-0.5">
            {WINDOWS.map((h) => (
              <button
                key={h}
                onClick={() => setHours(h)}
                className={`rounded-md px-3 py-1 text-xs font-medium transition ${
                  hours === h ? "bg-accent/15 text-accent" : "text-canvas-muted hover:text-canvas-ink"
                }`}
              >
                {h}h
              </button>
            ))}
          </div>
        }
      />

      {error && <div className="mb-4"><ErrorState message={error} /></div>}
      {loading && !data && <SkeletonRows rows={6} height={90} />}

      {data && (
        <>
          <div className="mb-5 grid grid-cols-2 gap-2 sm:grid-cols-4">
            <Metric label="Requests" value={data.summary.total} />
            <Metric
              label="Success rate"
              value={formatPct(data.summary.successRate)}
              tone={data.summary.successRate >= 0.95 ? "ok" : "warn"}
            />
            <Metric
              label="Fallback rate"
              value={formatPct(data.summary.total ? data.summary.fallbacks / data.summary.total : 0)}
              hint={`${data.summary.fallbacks} fallbacks`}
            />
            <Metric label="Cache hits" value={data.summary.cacheHits} />
            <Metric label="Avg latency" value={formatMs(data.summary.avgLatencyMs)} />
            <Metric label="Avg TTFT" value={formatMs(data.summary.avgTtftMs)} />
            <Metric
              label="Free requests"
              value={data.summary.freeCount}
              tone="ok"
              hint={
                data.summary.total
                  ? formatPct(data.summary.freeCount / data.summary.total) + " of traffic"
                  : undefined
              }
            />
            <Metric label="Cost" value={formatUsd(data.summary.totalCostUsd)} />
          </div>

          {/* Traffic */}
          <section className="panel mb-5 p-4">
            <SectionHeader
              title="Traffic"
              subtitle={`Requests per hour, last ${hours}h`}
              right={
                data.summary.total > 0 && (
                  <span className="text-xs text-canvas-muted">
                    {data.summary.success} succeeded
                  </span>
                )
              }
            />
            {data.timeseries.length === 0 ? (
              <p className="py-6 text-center text-sm text-canvas-muted">
                No traffic in this window.
              </p>
            ) : (
              <Bars
                data={data.timeseries.map((t) => ({
                  label: t.hour.slice(11) + ":00",
                  value: t.total,
                  title: `${t.hour.slice(5, 16)}:00 — ${t.total} req · ${formatPct(t.successRate)} · ${formatMs(t.avgLatencyMs)}`,
                  tone: t.successRate < 0.8 ? "warn" : undefined,
                }))}
              />
            )}
          </section>

          {/* Free vs paid split */}
          {data.summary.total > 0 && (
            <section className="panel mb-5 p-4">
              <SectionHeader title="Free vs paid" subtitle="Where request capacity came from" />
              <div className="flex h-3 overflow-hidden rounded-full bg-canvas">
                <div
                  className="bg-accent transition-all"
                  style={{ width: `${(data.summary.freeCount / data.summary.total) * 100}%` }}
                  title={`Free: ${data.summary.freeCount}`}
                />
                <div
                  className="bg-info/70 transition-all"
                  style={{
                    width: `${((data.summary.total - data.summary.freeCount) / data.summary.total) * 100}%`,
                  }}
                  title={`Paid: ${data.summary.total - data.summary.freeCount}`}
                />
              </div>
              <div className="mt-2 flex gap-4 text-xs text-canvas-muted">
                <span className="flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full bg-accent" /> Free tier{" "}
                  <span className="mono text-canvas-ink">{data.summary.freeCount}</span>
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full bg-info" /> Paid / BYOK{" "}
                  <span className="mono text-canvas-ink">
                    {data.summary.total - data.summary.freeCount}
                  </span>
                </span>
              </div>
            </section>
          )}

          {/* Provider performance */}
          <section className="panel mb-5 overflow-x-auto">
            <div className="panel-header">
              <h2 className="text-sm font-semibold">Provider performance</h2>
              <Link to="/providers" className="text-xs text-accent hover:underline">
                Providers →
              </Link>
            </div>
            <table className="w-full">
              <thead>
                <tr className="border-b border-canvas-border">
                  <th className="table-th">Provider</th>
                  <th className="table-th">Requests</th>
                  <th className="table-th">Success</th>
                  <th className="table-th">Avg latency</th>
                  <th className="table-th">Fallbacks</th>
                  <th className="table-th">Free</th>
                  <th className="table-th">Cost</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-canvas-border">
                {(data.providerUsage ?? []).length === 0 && (
                  <tr>
                    <td colSpan={7} className="table-td text-canvas-muted">
                      No provider traffic in this window.
                    </td>
                  </tr>
                )}
                {(data.providerUsage ?? []).map((p) => (
                  <tr key={p.providerId} className="transition hover:bg-canvas-raised">
                    <td className="table-td font-medium">
                      <Link
                        to={`/requests?provider=${p.providerTypeId ?? ""}`}
                        className="hover:text-accent hover:underline"
                      >
                        {p.providerDisplayName}
                      </Link>
                      {p.providerTypeId?.startsWith("mock-") && (
                        <span className="chip-muted ml-1.5 !text-[10px]">DEMO</span>
                      )}
                    </td>
                    <td className="table-td mono">{p.requests}</td>
                    <td className="table-td">
                      <StatusBadge
                        label={formatPct(p.requests ? p.success / p.requests : 1)}
                        tone={toneForStatus(p.requests === p.success ? "success" : "warn")}
                      />
                    </td>
                    <td className="table-td mono">{formatMs(p.avgLatencyMs)}</td>
                    <td className="table-td mono">{p.fallbacks}</td>
                    <td className="table-td mono text-accent">{p.freeCount}</td>
                    <td className="table-td mono">{formatUsd(p.costUsd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          {/* Model usage */}
          <section className="panel overflow-x-auto">
            <div className="panel-header">
              <h2 className="text-sm font-semibold">Model usage</h2>
              <Link to="/models" className="text-xs text-accent hover:underline">
                Models →
              </Link>
            </div>
            <table className="w-full">
              <thead>
                <tr className="border-b border-canvas-border">
                  <th className="table-th">Model</th>
                  <th className="table-th">Requests</th>
                  <th className="table-th">Success</th>
                  <th className="table-th">Avg latency</th>
                  <th className="table-th">Free</th>
                  <th className="table-th">Cost</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-canvas-border">
                {data.modelUsage.length === 0 && (
                  <tr>
                    <td colSpan={6} className="table-td text-canvas-muted">
                      No model usage yet.
                    </td>
                  </tr>
                )}
                {data.modelUsage.map((m, i) => (
                  <tr key={i} className="transition hover:bg-canvas-raised">
                    <td className="table-td">
                      <div className="font-medium">{m.requestedModel}</div>
                      <div className="mono text-[11px] text-canvas-muted">{m.modelId}</div>
                    </td>
                    <td className="table-td mono">{m.requests}</td>
                    <td className="table-td mono">{m.success}</td>
                    <td className="table-td mono">{formatMs(m.avgLatencyMs)}</td>
                    <td className="table-td mono text-accent">{m.freeCount}</td>
                    <td className="table-td mono">{formatUsd(Number(m.costUsd))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        </>
      )}
    </div>
  );
}
